import { useState } from 'react'

import Link from 'next/link'

import { cx } from '@linaria/core'
import { ClipLoader } from 'react-spinners'

import { useFoodSWR } from '~/api-client/food_api'
import { User } from '~/api-definition/definition'
import { Button } from '~/components/action/Button'
import { DinnerMemberFilter } from '~/libs/dinner'

import { DinnerSummary } from './DinnerSummary'

export const ScheduledDinnerList: React.FC<{ user: User; isMe: boolean }> = (
  props
) => {
  const options = props.isMe
    ? ([
        {
          value: 'organized',
          label: '幹事',
        },
        {
          value: 'attended',
          label: '参加予定',
        },
        {
          value: 'applied',
          label: '応募中',
        },
        {
          value: 'invited',
          label: '誘われ中',
        },
      ] as const)
    : ([
        {
          value: 'organized',
          label: '幹事',
        },
        {
          value: 'attended',
          label: '参加予定',
        },
      ] as const)

  const [filters, setFilters] = useState<Set<DinnerMemberFilter>>(
    new Set(options.map((opt) => opt.value))
  )

  const userDinners = useFoodSWR(
    '/user/dinners',
    {
      userId: props.user.userId,
      passedScheduledAt: false,
      sortBy: 'scheduled_at_asc',
      memberFilter:
        filters.size > 0 ? Array.from(filters).join(',') : undefined,
    },
    {
      swrOptions: {
        // NOTE:
        // ページ末尾に表示されることもあって、過去のデータを保持しないと
        // filter が切り替わったタイミングで件数が0件になりページが短かくなり
        // ロードが終わって新しいデータが表示されるとページが伸びてスクロール位置が変わってしまう。
        keepPreviousData: true,
      },
    }
  )

  return (
    <>
      <div className=" max-w-[400px]">
        <div className="mb-4 font-bold">食事会予定</div>
        <div className="grid grid-cols-4 gap-x-4 mb-7">
          {options.map((opt) => {
            return (
              <Button
                key={opt.value}
                variant="functionWeak"
                className={cx(
                  'w-auto min-w-0 text-sm',
                  filters.has(opt.value)
                    ? 'text-white bg-function-cv1 hover:text-white hover:bg-function-cv1'
                    : 'bg-function-weak text-text font-normal'
                )}
                onClick={() => {
                  setFilters((prev) => {
                    const newSet = new Set(prev)
                    if (newSet.has(opt.value)) {
                      newSet.delete(opt.value)
                    } else {
                      newSet.add(opt.value)
                    }
                    return newSet
                  })
                }}
              >
                {opt.label}
              </Button>
            )
          })}
        </div>
      </div>
      <div className="relative">
        {userDinners.isLoading && (
          <div className="absolute flex items-center justify-center w-full h-full">
            <ClipLoader
              color="#333"
              loading={true}
              size={32}
              aria-label="Loading Spinner"
            />
          </div>
        )}
        {/* フィルタが全部 OFF になった時は 0 件表示とする */}
        {/* https://foodies-prime.slack.com/archives/C049CGDL544/p1693198184979229?thread_ts=1693190601.698329&cid=C049CGDL544 */}
        {(filters.size === 0 || userDinners.data?.dinners.length === 0) && (
          <div className="mb-4 text-sm text-black">
            <p>該当する食事会がありません</p>
          </div>
        )}
        {filters.size > 0 &&
          userDinners.data?.dinners.map((dinner, i, arry) => {
            return (
              <div key={dinner.dinnerId}>
                <DinnerSummary
                  showParticipationStatus={props.isMe}
                  dinner={dinner}
                  showDate={true}
                />
                <div
                  className={cx(
                    'w-full my-5',
                    arry.length - 1 !== 0 &&
                      'border-b border-dashed border-[#CCCCCC]'
                  )}
                />
              </div>
            )
          })}
      </div>

      <div className="flex justify-center mt-5 mb-10">
        <Button variant="functionWeak" className="w-[240px]">
          {props.isMe ? (
            <Link href="/account/dinners">すべての食事会を見る</Link>
          ) : (
            <Link href={`/foodies/${props.user.userName}/dinners`}>
              すべての食事会を見る
            </Link>
          )}
        </Button>
      </div>
    </>
  )
}
